/**
 * Ciclo de vida de uma revenda (G01, §18 — o gate da etapa G).
 *
 * ── Este é o único contrato que não é sobre o DENTRO de uma revenda ───────
 *
 * Todo o resto de `/v1` descreve o que uma revenda faz com os próprios dados.
 * Aqui é a Operação Cliqfy criando e encerrando revendas — outro ator, outro
 * assunto e, por isso, outro prefixo: `/v1/operacao`, e não `/v1/admin`.
 *
 * A separação é deliberada. `/v1/admin/...` é a área do `admin_tenant`, e é a
 * área que a matriz da B16 descreve. Nenhum cliente provisiona ou apaga revenda
 * — nem a própria. Pôr estas rotas debaixo do mesmo prefixo convidaria alguém a
 * um dia ligá-las à matriz, e ali elas seriam permissão concedível a um cliente.
 *
 * ── Duas saídas para encerrar, e elas não se parecem (decisão B2) ─────────
 *
 * `encerrar` desliga e preserva; `excluir` apaga e não volta. A G01 pede que a
 * segunda *"não seja o mesmo botão do anterior nem esteja no mesmo lugar da
 * interface"*, e o contrato faz a parte dele: são rotas diferentes, com corpos
 * diferentes, e a exclusão exige step-up (B17) e uma revenda já encerrada.
 */

import { z } from 'zod';
import { IdSchema } from './comum.js';
import { PrimeiroAcessoSchema } from './administracao.js';
import {
  CadastroInformadoSchema,
  ContatoFinanceiroInformadoSchema,
  ContratacaoDaOrganizacaoSchema,
  TelefoneInformadoSchema,
} from './organizacoes.js';

/* ── Provisionar ─────────────────────────────────────────────────────────── */

/**
 * O identificador da revenda, que vira nome de realm e aparece em URL.
 *
 * Restrito de propósito: maiúscula, acento e espaço no nome do realm produzem
 * comportamento diferente conforme a versão do Keycloak, e um slug com barra
 * quebraria o caminho da Admin API sem erro claro.
 */
export const SlugDaRevendaSchema = z
  .string()
  .trim()
  .regex(
    /^[a-z0-9](?:[a-z0-9-]{1,30}[a-z0-9])$/,
    'Apenas letras minúsculas, números e hífen, entre 3 e 32 caracteres, começando e ' +
      'terminando por letra ou número.',
  );

export const ProvisionarRevendaSchema = z.object({
  slug: SlugDaRevendaSchema,
  /** O nome fantasia: o nome que aparece no CliqCRM. */
  nome: z.string().trim().min(2).max(120),
  /**
   * O plano em texto livre, da criação de antes do catálogo. Com `contrato`, o
   * plano é o do catálogo, e este campo pode ficar de fora; sem `contrato`, ele
   * é obrigatório, e a organização nasce sem contrato, como antes da #769.
   */
  plano: z.string().trim().min(1).max(60).optional(),
  /** Quem vai administrar a revenda. Vira conta no Keycloak e no Chatwoot. */
  admin: z.object({
    nome: z.string().trim().min(2).max(120),
    email: z.string().trim().email(),
    /**
     * Opcional. Fica no cadastro da pessoa, com a conta (decisão de
     * 27/10/2026); a conta que já existia fica como está, com ou sem telefone.
     */
    telefone: TelefoneInformadoSchema.optional(),
  }),
  /** As origens que o realm vai aceitar no login desta revenda. */
  origensWeb: z.array(z.string().url()).min(1),
  /**
   * "Sim, sei que o e-mail do administrador já tem conta no CliqCRM, em outra
   * organização, e quero que ela administre também esta."
   *
   * Sem isto, um e-mail que já tem conta é recusado com `CONFIRMACAO_EXIGIDA`.
   * Confirmando, a mesma conta, com a mesma senha, ganha o vínculo de
   * administrador com a organização nova, e a pessoa escolhe a organização ao
   * entrar. É o `confirmarSegundaConta` do convite de pessoas, aqui.
   */
  confirmarContaExistente: z.boolean().optional(),
  /**
   * "Sim, sei que este identificador é de uma organização encerrada, e quero
   * continuar a criação dela" (decisão de 27/10/2026, #1118).
   *
   * Retomar uma encerrada traz de volta o que ela guarda e as pessoas que tinham
   * acesso. Sem isto, a retomada é recusada com `CONFIRMACAO_EXIGIDA`, e o erro
   * aponta este campo, para a tela não confundir com a pergunta da conta. A
   * organização com outro CNPJ continua recusada, com ou sem confirmação.
   */
  confirmarRetomadaDeEncerrada: z.boolean().optional(),
  /*
   * ── A criação com contrato (#769) ───────────────────────────────────────
   *
   * O cadastro da empresa, o fuso, o contrato do catálogo e o contato
   * financeiro vão juntos: com `contrato`, `cadastro` e `financeiro` são
   * obrigatórios, e a API recusa o pedido sem eles. Ficam opcionais aqui só
   * porque a criação de antes, com o plano em texto, continua aceita (régua do
   * `/v1`). A tela manda sempre.
   */
  /** A razão social, o CNPJ (de nenhuma outra organização) e o segmento. */
  cadastro: CadastroInformadoSchema.optional(),
  /**
   * O fuso da organização, no nome IANA. Sem ele, a organização nova fica com
   * "America/Sao_Paulo", e a retomada não mexe no fuso que ela já tem.
   */
  fuso: z.string().trim().min(1).max(64).optional(),
  contrato: ContratacaoDaOrganizacaoSchema.optional(),
  financeiro: ContatoFinanceiroInformadoSchema.optional(),
});

export type ProvisionarRevenda = z.infer<typeof ProvisionarRevendaSchema>;

export const RevendaProvisionadaSchema = z.object({
  tenantId: IdSchema,
  slug: SlugDaRevendaSchema,
  /**
   * `ativo` quando a saga concluiu; `provisionando` quando não.
   *
   * Não existe estado de "falhou", e não foi inventado um: `provisionando` já
   * diz a verdade — começou e não terminou — e é dele que a retomada parte.
   */
  status: z.enum(['ativo', 'provisionando']),
  /** O id em `core.saga_state`, para achar o que aconteceu passo a passo. */
  saga: z.string(),
  /**
   * Quanto tempo a saga levou, do primeiro passo ao último.
   *
   * A G01 pede *"tempo total medido"*, e medir uma vez à mão responderia a
   * pergunta de hoje e nenhuma das seguintes. Aqui a medida sai em toda
   * execução — na resposta e em `audit_logs` — então a décima revenda é
   * comparável com a primeira, e uma que degradou aparece sozinha.
   *
   * Só o tempo da saga: a validação e a criação do tenant vêm antes, e são
   * escrita local em milissegundos. O que este número mede é o que depende das
   * engines, que é a parte que varia.
   */
  duracaoMs: z.number().int().nonnegative(),
  /**
   * Passos que ficaram feitos e não puderam ser desfeitos.
   *
   * Vazio é o normal — os cinco passos da G01 são compensáveis. Não-vazio
   * significa que a compensação falhou e sobrou efeito numa engine: é o estado
   * que a B28 criou para pedir gente, e reprovisionar por cima é recusado.
   */
  irreversiveis: z.array(z.string()),
  /** O que ainda precisa de gente, mesmo quando tudo deu certo. */
  pendencias: z.array(z.string()),
  /**
   * Como o administrador inicial entra, quando esta execução o criou.
   *
   * Mesmo formato do convite de pessoa: com `senha_provisoria`, mostre UMA vez
   * e não guarde. Ausente quando o administrador não foi criado agora (a
   * criação não terminou, ou retomou depois desse passo).
   */
  primeiroAcesso: PrimeiroAcessoSchema.optional(),
});

export type RevendaProvisionada = z.infer<typeof RevendaProvisionadaSchema>;

/**
 * A resposta de retomar a criação pela gestão (#769): a mesma da criação, e,
 * junto com o primeiro acesso, o nome do administrador do pedido guardado. A
 * tela da gestão não tem o pedido, e precisa do nome para entregar o primeiro
 * acesso; sem ele, o nome não vai.
 */
export const CriacaoRetomadaSchema = RevendaProvisionadaSchema.extend({
  nome_do_administrador: z.string().optional(),
});

export type CriacaoRetomada = z.infer<typeof CriacaoRetomadaSchema>;

/* ── Encerrar e excluir ──────────────────────────────────────────────────── */

/**
 * A justificativa vai para `audit_logs`, que não se edita depois.
 *
 * O mínimo de 10 caracteres é o mesmo CHECK da migração 008
 * (`audit_justificativa_real`): justificativa vazia é pior que ausente, porque
 * parece preenchida numa auditoria.
 */
export const EncerrarRevendaSchema = z.object({
  justificativa: z.string().trim().min(10).max(500),
});

export type EncerrarRevenda = z.infer<typeof EncerrarRevendaSchema>;

/**
 * O que aconteceu em cada um dos quatro sistemas.
 *
 * `dispensado` não é `aplicado` com outro nome: ele marca o que não tinha o que
 * fazer — a revenda sem conexão de Chatwoot, o realm que já não existia. O
 * sistema `crm` é o espaço legado da engine de cadastro que saiu: ele volta
 * `falhou` quando há um a apagar à mão, e somar tudo num "ok" o esconderia.
 */
export const EfeitoNoSistemaSchema = z.object({
  sistema: z.enum(['keycloak', 'chatwoot', 'crm', 'postgres']),
  resultado: z.enum(['aplicado', 'dispensado', 'falhou']),
  detalhe: z.string(),
});

export type EfeitoNoSistema = z.infer<typeof EfeitoNoSistemaSchema>;

/** Os quatro sistemas que uma revenda ocupa. */
export type Sistema = EfeitoNoSistema['sistema'];

export const RevendaDesprovisionadaSchema = z.object({
  tenantId: IdSchema,
  slug: SlugDaRevendaSchema,
  status: z.enum(['ativo', 'encerrado']),
  sistemas: z.array(EfeitoNoSistemaSchema),
  /**
   * `true` quando algum sistema falhou.
   *
   * No encerramento significa que alguém talvez ainda autentique; na exclusão,
   * que sobrou coisa viva numa engine. Os dois são incidente, não pendência —
   * a diferença entre este campo e `pendencias` é essa.
   */
  alerta: z.boolean(),
  pendencias: z.array(z.string()),
});

export type RevendaDesprovisionada = z.infer<typeof RevendaDesprovisionadaSchema>;

/* ── Ação segura a partir do painel (G04) ────────────────────────────────── */

/**
 * O que uma reconciliação forçada mexeu.
 *
 * Números, e não "ok". A G04 pede *"ação segura a partir do painel"*, e o que
 * faz a ação ser segura de operar às três da manhã é a confirmação: quem clicou
 * precisa sair sabendo se o backlog andou, não se o botão respondeu.
 */
export const ReconciliacaoForcadaSchema = z.object({
  /** Eventos em `received` que voltaram para a fila. */
  promovidos: z.number().int().nonnegative(),
  /** Eventos presos em `processing` cujo lock venceu e foi liberado. */
  locksRetomados: z.number().int().nonnegative(),
  /**
   * Eventos sem payload para republicar.
   *
   * A purga de 90 dias (ADR-0009) tira o payload e deixa a linha. Não é erro —
   * é o prazo funcionando —, mas quem clicou precisa saber que esses não voltam,
   * em vez de ficar esperando por eles.
   */
  semPayload: z.number().int().nonnegative(),
  falhas: z.number().int().nonnegative(),
});

export type ReconciliacaoForcada = z.infer<typeof ReconciliacaoForcadaSchema>;

/* ── Completar a configuração de uma organização que já existe ──────────── */

/**
 * Pelo identificador, no corpo, e não por id no caminho: quem usa a tela sabe
 * o identificador, e é ele que a criação também usa.
 */
export const CompletarRevendaSchema = z.object({
  slug: SlugDaRevendaSchema,
});

export type CompletarRevenda = z.infer<typeof CompletarRevendaSchema>;

/**
 * O que foi feito, item por item. `dispensado` é "já estava feito", e não
 * falha: rodar de novo numa organização completa responde só dispensados.
 */
export const RevendaCompletadaSchema = z.object({
  tenantId: IdSchema,
  slug: SlugDaRevendaSchema,
  itens: z.array(
    z.object({
      item: z.enum([
        'integracao_do_atendimento',
        'atendentes',
        'aviso_do_cadastro',
        'aviso_do_atendimento',
      ]),
      resultado: z.enum(['aplicado', 'dispensado', 'falhou']),
      detalhe: z.string(),
    }),
  ),
});

export type RevendaCompletada = z.infer<typeof RevendaCompletadaSchema>;
